"use client";

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, ShoppingCart, Star } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { toast } from "sonner";
import { getLocaleFromPathname, hrefWithLocale } from "@/lib/i18n";

interface PromptCardProps {
  id: string;
  title: string;
  description: string;
  price: number;
  category: string;
  rating: number;
  reviewCount: number;
  author: string;
  thumbnail?: string;
  isFavorited?: boolean;
  onAddToCart?: (id: string) => boolean | void;
  onToggleFavorite?: (id: string) => void;
}

export function PromptCard({
  id,
  title,
  description,
  price,
  category,
  rating,
  reviewCount,
  author,
  thumbnail,
  isFavorited = false,
  onAddToCart,
  onToggleFavorite,
}: PromptCardProps) {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname);

  const handleAddToCart = () => {
    const result = onAddToCart?.(id);
    if (result === false) {
      toast.info("이미 장바구니에 있는 상품입니다");
    }
  };

  return (
    <Card className="group hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="p-0">
        <div className="relative aspect-video bg-muted rounded-t-lg overflow-hidden">
          {thumbnail ? (
            <Image
              src={thumbnail || "/placeholder.svg"}
              alt={title}
              width={640}
              height={360}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
              <span className="text-muted-foreground text-sm">
                프롬프트 미리보기
              </span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            className={`absolute top-2 right-2 h-8 w-8 ${
              isFavorited ? "text-red-500" : "text-foreground"
            } hover:text-red-500`}
            onClick={() => onToggleFavorite?.(id)}
            aria-label={isFavorited ? "즐겨찾기 해제" : "즐겨찾기 추가"}
          >
            <Heart className={`h-4 w-4 ${isFavorited ? "fill-current" : ""}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="secondary" className="text-xs">
            {category}
          </Badge>
          <div className="flex items-center space-x-1">
            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
            <span className="text-xs text-muted-foreground">
              {rating} ({reviewCount})
            </span>
          </div>
        </div>

        <Link href={hrefWithLocale(locale, `/prompt/${id}`)}>
          <h3 className="font-semibold text-sm mb-2 line-clamp-2 hover:text-primary transition-colors">
            {title}
          </h3>
        </Link>

        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
          {description}
        </p>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>by {author}</span>
          <span className="font-semibold text-foreground">
            ₩{price.toLocaleString()}
          </span>
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0">
        <Button className="w-full" size="sm" onClick={handleAddToCart}>
          <ShoppingCart className="h-4 w-4 mr-2" />
          장바구니 담기
        </Button>
      </CardFooter>
    </Card>
  );
}
